import { Injectable } from "@angular/core";
import { HttpService } from "./http.service";
import { environment } from "@environments/environment";
import { RegisterDTO, User, UserInfo, VerifyTokenDTO, Workspace } from "@models/user";
import { Observable, map, switchMap, of, tap } from "rxjs";
import { Router } from "@angular/router";
import { AuthDTO } from "../request-dtos/authentication.dto";
import { SessionService } from "./session.service";
import { UserService } from "./user.service";
import { PermissionService } from "./permissions.service";
import { AppInitService } from "./app-init.service";
import { BrandingConfigService } from "./branding-config.service";
import { GtmService } from "./gtm.service";
import { AppRealtimeService } from "./app-realtime.service";
import { NativePushService } from "./native-push.service";

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {

    constructor(private http: HttpService,
        private sessionSvc: SessionService,
        private router: Router,
        private userSvc: UserService,
        private permissionSvc: PermissionService,
        private appInitService: AppInitService,
        private brandingConfigService: BrandingConfigService,
        private gtmService: GtmService,
        private appRealtime: AppRealtimeService,
        private nativePush: NativePushService,
    ) {
    }

    /**
    *  Login user
    * @param email 
    * @param password 
    * @param rememberMe 
    * @param recaptchaToken 
    * @returns 
    */
    public login(email: string, password: string, rememberMe: boolean = true, recaptchaToken?: string | null): Observable<User> {
        const body: any = { email, password };
        if (recaptchaToken) {
            body.recaptchaToken = recaptchaToken;
        }
        return this.http.post(`${environment.apiUrl}/auth/login`, body).pipe(
            switchMap((user: any) => {
                let userInfo: User = user.data;
                this.setUserSession(userInfo, rememberMe);
                return this.selectInitialWorkspace(userInfo, rememberMe).pipe(
                    tap(() => {
                        // Refresh branding after successful login
                        this.refreshBrandingOnLogin(userInfo.user.id.toString());
                    }),
                    map(() => userInfo)
                );
            })
        );
    }

    /**
     * Sets user session and selects the appropriate workspace
     * Uses existing pattern - no changes to service calls needed
     */
    public setUserSession(userInfo: User, rememberMe: boolean = true) {
        this.sessionSvc.setUserValue(userInfo, rememberMe);
    }

    /**
     * Selects the initial workspace based on user preferences
     * If user has lastSelectedWorkspace, use that, otherwise use first workspace
     */
    private selectInitialWorkspace(userInfo: User, rememberMe: boolean = true): Observable<void> {
        if (!userInfo.user || !userInfo.user.workspaces || userInfo.user.workspaces.length === 0) {
            return of(void 0);
        }

        let workspaceToSelect: Workspace;


         if(userInfo?.user?.workspaces && userInfo?.user?.workspaces.length > 0){ 
            if (userInfo.lastSelectedWorkspace && userInfo.lastSelectedWorkspace > 0) {
                const lastWorkspace = userInfo.user.workspaces.find(w => w.id === userInfo.lastSelectedWorkspace);
                if (lastWorkspace) {
                    workspaceToSelect = lastWorkspace;
                }
            }
            else {
                workspaceToSelect = userInfo.user.workspaces[0];
            }
        }
        
        return this.userSvc.selectWorkspace(workspaceToSelect.uid).pipe(
            tap(({selectedWorkspace, role}) => {
                this.sessionSvc.setUserWorkspace(selectedWorkspace);
                // Pass rememberMe parameter to permission service
                this.permissionSvc.setPermissions(role.permissions, rememberMe);
                // Workspace join is sent once from AppRealtimeService on `authenticated`.
                this.appRealtime.ensureConnected();
            }),
            map(() => void 0)
        );
    }

    public register(user: RegisterDTO, recaptchaToken?: string | null): Observable<User> {
        const body: any = { ...user };
        if (recaptchaToken) {
            body.recaptchaToken = recaptchaToken;
        }
        return this.http.post(`${environment.apiUrl}/auth/register`, body).pipe(map((user: any) => {
            // let userData: User = user.data;
            // // let {user: userInfo, ...rest} = userData;
            // this.sessionSvc.setUserValue(<any>{user: userData}, true);

            /** Return user data */
            return user.data;
        }));
    }

    /**
     * Updates password of the user
     * @param authDTO 
     * @returns 
     */
    public updatePassword(authDTO: AuthDTO): Observable<User> {   
        return this.http.post(`${environment.apiUrl}/auth/update-password`, authDTO).pipe(map((userData: any)=>{
            let user: User = userData.data;
            return user;
        }));
    }  

    public resetToken() {
        return this.http.get(`${environment.apiUrl}/auth/resetToken`).pipe(
            switchMap((user: any) => {
                let userInfo: User = user.data;
                this.setUserSession(userInfo, true);
                return this.selectInitialWorkspace(userInfo, true).pipe(
                    map(() => user)
                );
            })
        );
    }

    public googleSSOLogin(code: string, isSignup: boolean = false) {
        return this.http.get(`${environment.apiUrl}/auth/google/callback?code=${code}`).pipe(
            switchMap((user: any) => {
                let userInfo: User = user.data;
                
                // Track Google signup event if this is a signup flow
                // Check if user has no existing workspaces (new user) or if explicitly marked as signup
                const isNewUser = !userInfo.user?.workspaces || userInfo.user.workspaces.length === 0;
                if (isSignup || isNewUser) {
                    this.gtmService.pushSignUp('google');
                }
                
                this.setUserSession(userInfo, true);
                return this.selectInitialWorkspace(userInfo, true).pipe(
                    tap(() => {
                        // Refresh branding after successful login
                        this.refreshBrandingOnLogin(userInfo.user.id.toString());
                    }),
                    map(() => userInfo)
                );
            })
        );
    }

    /**
     * Verify user token
     * @param token 
     * @returns 
     * 
     * This method verifies the user token and logs in the user if the token is valid.
     * If the token is invalid, it will return an error.
     * 
     * The method will return the user object if the token is valid.
     * 
     * @example
     * this.authenticationSvc.verifyUserToken(token).subscribe((user: User) => {
     *     console.log(user);
     * });
     */
    public verifyUserToken(token: string) {
        return this.http.post(`${environment.apiUrl}/auth/verifyToken?token=${token}`, {}).pipe(
            switchMap((user: any) => {
                let userInfo: User = user.data;
                this.setUserSession(userInfo, true);
                return this.selectInitialWorkspace(userInfo, true).pipe(
                    tap(() => {
                        // Refresh branding after successful login
                        this.refreshBrandingOnLogin(userInfo.user.id.toString());
                    }),
                    map(() => user)
                );
            })
        );
    }

    public forgotPassword(email:string) {
        return this.http.post(`${environment.apiUrl}/auth/forgotPassword`, {email}).pipe(map((user: any)=>{
            let userInfo: User = user.data;
            return userInfo;
        }));
    }

    public resetPassword(token: string, password: string) {
        return this.http.post(`${environment.apiUrl}/auth/updatePassword?token=${token}`, {password}).pipe(map((user: any)=>{
            let userInfo: User = user.data;
            return userInfo;
        }));
    }

    public sendConfirmationEmail() {
        console.log(this.sessionSvc.getUserValue());
        return this.http.get(`${environment.apiUrl}/auth/sendConfirmationEmail/${this.sessionSvc.getUserValue().user.id}`).pipe(map((userData: any)=>{
            let user: UserInfo = userData.data;
            return user;
        }));
    }

    /**
     * Accept invitation for a user who was invited via email
     * @param token 
     * @returns 
     * 
     * This method accepts the invitation token and logs in the user if the token is valid.
     * If the token is invalid, it will return an error.
     * 
     * The method will return the user object if the token is valid.
     * 
     * @example
     * this.authenticationSvc.acceptInvitation(token).subscribe((user: User) => {
     *     console.log(user);
     * });
     */
    public acceptInvitation(token: string) {
        return this.http.get(`${environment.apiUrl}/auth/accept-invite?token=${token}`, {}).pipe(
            switchMap((user: any) => {
                let userInfo: User = user.data;
                this.setUserSession(userInfo, true);
                return this.selectInitialWorkspace(userInfo, true).pipe(
                    tap(() => {
                        // Refresh branding after successful login
                        this.refreshBrandingOnLogin(userInfo.user.id.toString());
                    }),
                    map(() => user)
                );
            })
        );
    }

    public logout() {
        this.appRealtime.disconnect();
        this.nativePush.resetForNextSession();
        this.sessionSvc.removeUser();
        this.router.navigate(["/auth/login"]);
    }

    /**
     * Refresh branding configuration when user logs in
     */
    private refreshBrandingOnLogin(userId: string): void {
        this.appInitService.getBrandingConfigOnLogin(userId);
    }

    /**
     * Refresh user data after subscription upgrade or other profile changes
     * This method fetches the latest user information from the server and updates the session
     * without requiring a full logout/login cycle
     */
    public refreshUserData(): Observable<User> {
        return this.resetToken().pipe(
            map((response: any) => response.data)
        );
    }
}